// Global Instructions Rule Applied!
//
// In-app help — fetches one cfg-authored guide for the "?" drawer.
//
// Lazy by design: nothing is requested until the drawer is actually opened
// (`enabled`), and the service caches the result for the session, so reopening
// the same guide is free.

import { useEffect, useState } from 'react'
import { store } from '../../state_manager/store.ts'
import { getAccessToken } from '../auth/authService'
import { fetchHelpArticle } from '../services/helpService.ts'
import type { HelpArticle } from '../../state_manager/data_models/help_article_model.ts'

export interface UseHelpArticleResult {
  article: HelpArticle | null
  loading: boolean
  error: string | null
}

export const useHelpArticle = (
  articleKey: string | undefined,
  enabled: boolean
): UseHelpArticleResult => {
  const [article, setArticle] = useState<HelpArticle | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !articleKey) return

    let cancelled = false
    setLoading(true)
    setError(null)

    const run = async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchHelpArticle(articleKey, token)
      if (cancelled) return
      setArticle(res.article)
      setError(res.error ?? null)
      setLoading(false)
    }

    run()
    return () => {
      cancelled = true
    }
  }, [articleKey, enabled])

  return { article, loading, error }
}
